import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  DollarSign, Users, FileText, Download, CheckCircle, Clock, Calculator,
  Plus, Settings, CreditCard, Info, RefreshCw, Calendar, TrendingUp, 
  Banknote, UserCheck, AlertCircle, Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalarySlipPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [structures, setStructures] = useState([]);
  const [slips, setSlips] = useState([]);
  const [slipsPagination, setSlipsPagination] = useState(null);
  const [slipsPage, setSlipsPage] = useState(1);
  const [teachers, setTeachers] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Dialogs
  const [structureDialog, setStructureDialog] = useState(false);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [slipDetailDialog, setSlipDetailDialog] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  
  // Forms
  const [structureForm, setStructureForm] = useState({
    teacher_id: '', basic_salary: 0, hra: 0, da: 0, ta: 0,
    medical_allowance: 0, special_allowance: 0, pf_deduction: 0,
    professional_tax: 0, tds: 0, other_deductions: 0
  });
  const [settingsForm, setSettingsForm] = useState({});
  const [payForm, setPayForm] = useState({ payment_method: 'bank_transfer', payment_reference: '' });

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchSlips = async (page = 1) => {
    try {
      const res = await axios.get(`${API_URL}/api/salary/slips?month=${selectedMonth}&page=${page}&page_size=50`, { headers: getAuthHeader() });
      setSlips(res.data.items || []);
      setSlipsPagination({
        total: res.data.total,
        page: res.data.page,
        total_pages: res.data.total_pages,
        has_next: res.data.has_next,
        has_prev: res.data.has_prev
      });
      setSlipsPage(page);
    } catch (error) {
      console.error('Failed to fetch slips');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, structRes, slipsRes, teachRes, staffRes, settRes] = await Promise.all([
        axios.get(`${API_URL}/api/salary/dashboard?month=${selectedMonth}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/salary/structures`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/salary/slips?month=${selectedMonth}&page=1&page_size=50`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/salary/settings`, { headers: getAuthHeader() })
      ]);
      setDashboard(dashRes.data);
      setStructures(structRes.data);
      // Handle paginated response
      setSlips(slipsRes.data.items || []);
      if (slipsRes.data.total !== undefined) {
        setSlipsPagination({
          total: slipsRes.data.total,
          page: slipsRes.data.page,
          total_pages: slipsRes.data.total_pages,
          has_next: slipsRes.data.has_next,
          has_prev: slipsRes.data.has_prev
        });
      }
      const teacherList = teachRes.data.items || teachRes.data || [];
      const staffList = (staffRes.data.items || staffRes.data || []).map(s => ({
        ...s, staff_type: 'non_teaching'
      }));
      setTeachers([...teacherList, ...staffList]);
      setSettings(settRes.data);
      setSettingsForm(settRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Generate months for select
  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    months.push(d.toISOString().slice(0, 7));
  }

  const teachersWithoutStructure = teachers.filter(t => !structures.find(s => s.teacher_id === t.id));

  const generateSlip = async (teacherId) => {
    try {
      await axios.post(`${API_URL}/api/salary/generate/${teacherId}`, null, {
        headers: getAuthHeader(), params: { month: selectedMonth }
      });
      toast.success('Salary slip generated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate');
    }
  };

  const regenerateSlip = async (staffId) => {
    try {
      await axios.post(`${API_URL}/api/salary/regenerate/${staffId}`, null, {
        headers: getAuthHeader(), params: { month: selectedMonth }
      });
      toast.success('Salary slip recalculated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate');
    }
  };

  const regenerateBulk = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/salary/regenerate-bulk`, null, {
        headers: getAuthHeader(), params: { month: selectedMonth }
      });
      toast.success(`Recalculated ${res.data.regenerated} slips`);
      fetchData();
    } catch (error) {
      toast.error('Failed to regenerate');
    }
  };

  const generateBulk = async () => {
    try {
      await axios.post(`${API_URL}/api/salary/generate-bulk`, null, {
        headers: getAuthHeader(), params: { month: selectedMonth }
      });
      toast.success('Salary slips generated');
      setGenerateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate slips');
    }
  };

  const downloadPDF = async (slipId) => {
    try {
      const res = await axios.get(`${API_URL}/api/salary/slip/${slipId}/pdf`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `salary_slip_${slipId}.pdf`;
      link.click();
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const downloadBulkPDF = async () => {
    for (const slip of slips) {
      await downloadPDF(slip.id);
    }
  };

  const saveStructure = async () => {
    try {
      // Use staff_id in URL path (old format) for backward compatibility
      const staffId = structureForm.teacher_id;
      if (!staffId) {
        toast.error('Please select a staff member');
        return;
      }
      await axios.post(`${API_URL}/api/salary/structure/${staffId}`, null, { 
        headers: getAuthHeader(),
        params: {
          basic_salary: structureForm.basic_salary || 0,
          hra: structureForm.hra || 0,
          da: structureForm.da || 0,
          ta: structureForm.ta || 0,
          medical_allowance: structureForm.medical_allowance || 0,
          special_allowance: structureForm.special_allowance || 0,
          pf_deduction: structureForm.pf_deduction || 0,
          professional_tax: structureForm.professional_tax || 0,
          tds: structureForm.tds || 0,
          other_deductions: structureForm.other_deductions || 0
        }
      });
      toast.success('Salary structure saved');
      setStructureDialog(false);
      setStructureForm({ teacher_id: '', basic_salary: 0, hra: 0, da: 0, ta: 0, medical_allowance: 0, special_allowance: 0, pf_deduction: 0, professional_tax: 0, tds: 0, other_deductions: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/salary/settings`, settingsForm, { headers: getAuthHeader() });
      toast.success('Settings saved');
      setSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const markPaid = async () => {
    try {
      await axios.patch(`${API_URL}/api/salary/slip/${selectedSlip.id}/pay`, payForm, { headers: getAuthHeader() });
      toast.success('Marked as paid');
      setPayDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const gross = structureForm.basic_salary + structureForm.hra + structureForm.da + structureForm.ta + structureForm.medical_allowance + structureForm.special_allowance;
  const deductions = structureForm.pf_deduction + structureForm.professional_tax + structureForm.tds + structureForm.other_deductions;
  const net = gross - deductions;

  const formatMonth = (monthStr) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Slip Detail View Component
  const SlipDetailView = ({ slip }) => {
    if (!slip) return null;
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start border-b pb-4">
          <div>
            <h3 className="text-xl font-bold">{slip.teacher_name}</h3>
            <p className="text-sm text-muted-foreground">{slip.employee_id} • {slip.staff_type || 'Teacher'}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Slip No</p>
            <p className="font-mono font-medium">{slip.slip_number}</p>
          </div>
        </div>

        {/* Calculation Formula */}
        {slip.calculation && (
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800 dark:text-blue-200">Salary Calculation</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Monthly Gross</p>
                  <p className="font-semibold">₹{slip.calculation.monthly_gross?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Days in Month</p>
                  <p className="font-semibold">{slip.attendance?.total_days_in_month || 30}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Daily Rate</p>
                  <p className="font-semibold">₹{slip.calculation.daily_salary?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Present Days Salary</p>
                  <p className="font-semibold text-green-600">₹{slip.calculation.present_salary?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendance */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Calendar className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-2xl font-bold">{slip.attendance?.working_days || 0}</p>
              <p className="text-xs text-muted-foreground">Working Days</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 dark:bg-green-950">
            <CardContent className="pt-4 text-center">
              <UserCheck className="h-5 w-5 mx-auto text-green-600 mb-1" />
              <p className="text-2xl font-bold text-green-600">{slip.attendance?.present_days || 0}</p>
              <p className="text-xs text-muted-foreground">Present</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 dark:bg-red-950">
            <CardContent className="pt-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto text-red-600 mb-1" />
              <p className="text-2xl font-bold text-red-600">{slip.attendance?.absent_days || 0}</p>
              <p className="text-xs text-muted-foreground">Absent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Clock className="h-5 w-5 mx-auto text-amber-600 mb-1" />
              <p className="text-2xl font-bold text-amber-600">{slip.attendance?.late_days || 0}</p>
              <p className="text-xs text-muted-foreground">Late</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <FileText className="h-5 w-5 mx-auto text-blue-600 mb-1" />
              <p className="text-2xl font-bold text-blue-600">{slip.attendance?.leave_days || 0}</p>
              <p className="text-xs text-muted-foreground">Leave</p>
            </CardContent>
          </Card>
        </div>

        {/* Earnings & Deductions */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Earnings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-green-600">
                <TrendingUp className="h-4 w-4" /> Earnings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">Daily Rate</span>
                <span>₹{(slip.earnings?.daily_rate || slip.calculation?.daily_salary || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">Present Days ({slip.attendance?.present_days})</span>
                <span>₹{(slip.earnings?.present_days_salary || slip.calculation?.present_salary || 0).toLocaleString()}</span>
              </div>
              {slip.earnings?.overtime_payment > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Overtime ({slip.attendance?.overtime_hours}h)</span>
                  <span>₹{slip.earnings.overtime_payment.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between py-2 font-bold text-green-600 text-lg">
                <span>Total Earnings</span>
                <span>₹{(slip.earnings?.gross_earnings || 0).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Deductions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-red-600">
                <Banknote className="h-4 w-4" /> Deductions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {slip.deductions?.pf > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Provident Fund (PF)</span>
                  <span className="text-red-600">₹{slip.deductions.pf.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.professional_tax > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Professional Tax</span>
                  <span className="text-red-600">₹{slip.deductions.professional_tax.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.tds > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">TDS</span>
                  <span className="text-red-600">₹{slip.deductions.tds.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.late_deduction > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Late Penalty</span>
                  <span className="text-red-600">₹{slip.deductions.late_deduction.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.other_deductions > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Other</span>
                  <span className="text-red-600">₹{slip.deductions.other_deductions.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between py-2 font-bold text-red-600 text-lg">
                <span>Total Deductions</span>
                <span>₹{(slip.deductions?.total_deductions || 0).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Net Payable */}
        <Card className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
          <CardContent className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-emerald-100 text-sm">Net Payable</p>
                <p className="text-3xl font-bold">₹{(slip.net_payable || 0).toLocaleString()}</p>
              </div>
              <Badge variant={slip.payment_status === 'paid' ? 'secondary' : 'outline'} className="text-white border-white">
                {slip.payment_status === 'paid' ? '✓ PAID' : 'PENDING'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <DashboardLayout title="Salary Management">
      {/* Month Selector Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48" data-testid="month-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map(m => (
                <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={() => setSettingsDialog(true)}>
          <Settings className="h-4 w-4 mr-2" /> Settings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Overview</TabsTrigger>
          <TabsTrigger value="structures" data-testid="tab-structures">Structures</TabsTrigger>
          <TabsTrigger value="slips" data-testid="tab-slips">Salary Slips</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{structures.length}</p>
                      <p className="text-sm text-muted-foreground">Staff with Structure</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{slips.length}</p>
                      <p className="text-sm text-muted-foreground">Slips Generated</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{slips.filter(s => s.payment_status === 'paid').length}</p>
                      <p className="text-sm text-muted-foreground">Paid</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{slips.filter(s => s.payment_status === 'pending').length}</p>
                      <p className="text-sm text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Payment Summary & Actions */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Summary - {formatMonth(selectedMonth)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Total Payable</span>
                  <span className="text-xl font-bold">₹{(dashboard.total_payable || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Total Paid</span>
                  <span className="text-xl font-bold text-green-600">₹{(dashboard.total_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Pending Amount</span>
                  <span className="text-xl font-bold text-red-600">₹{(dashboard.total_pending || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full justify-start" variant="outline" onClick={() => setStructureDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Salary Structure
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setGenerateDialog(true)}>
                  <Calculator className="h-4 w-4 mr-2" /> Generate All Slips
                </Button>
                {slips.length > 0 && (
                  <Button className="w-full justify-start" variant="outline" onClick={regenerateBulk}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Recalculate All Slips
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Structures Tab */}
        <TabsContent value="structures" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Salary Structures ({structures.length})</h3>
            <Button onClick={() => setStructureDialog(true)} data-testid="add-structure-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Structure
            </Button>
          </div>

          <div className="grid gap-4">
            {structures.map(s => (
              <Card key={s.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{s.teacher_name}</p>
                        <p className="text-sm text-muted-foreground">{s.employee_id}</p>
                      </div>
                    </div>

                    {/* Salary Breakdown */}
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-4 text-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Basic</p>
                        <p className="font-medium">₹{s.basic_salary?.toLocaleString()}</p>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="cursor-help">
                              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                Allowances <Info className="h-3 w-3" />
                              </p>
                              <p className="font-medium text-blue-600">
                                ₹{((s.hra||0)+(s.da||0)+(s.ta||0)+(s.medical_allowance||0)+(s.special_allowance||0)).toLocaleString()}
                              </p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="p-3">
                            <p className="font-medium border-b pb-1 mb-2">Allowances</p>
                            <div className="space-y-1 text-xs">
                              <p>HRA: ₹{(s.hra||0).toLocaleString()}</p>
                              <p>DA: ₹{(s.da||0).toLocaleString()}</p>
                              <p>TA: ₹{(s.ta||0).toLocaleString()}</p>
                              <p>Medical: ₹{(s.medical_allowance||0).toLocaleString()}</p>
                              <p>Special: ₹{(s.special_allowance||0).toLocaleString()}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div>
                        <p className="text-xs text-muted-foreground">Gross</p>
                        <p className="font-medium">₹{s.gross_salary?.toLocaleString()}</p>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="cursor-help">
                              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                Deductions <Info className="h-3 w-3" />
                              </p>
                              <p className="font-medium text-red-600">₹{(s.total_deductions||0).toLocaleString()}</p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="p-3">
                            <p className="font-medium border-b pb-1 mb-2">Deductions</p>
                            <div className="space-y-1 text-xs">
                              <p>PF: ₹{(s.pf_deduction||0).toLocaleString()}</p>
                              <p>Prof. Tax: ₹{(s.professional_tax||0).toLocaleString()}</p>
                              <p>TDS: ₹{(s.tds||0).toLocaleString()}</p>
                              <p>Other: ₹{(s.other_deductions||0).toLocaleString()}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div>
                        <p className="text-xs text-muted-foreground">Net</p>
                        <p className="font-bold text-green-600">₹{s.net_salary?.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <Button size="sm" onClick={() => generateSlip(s.teacher_id)}>Generate Slip</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {teachersWithoutStructure.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950">
              <CardHeader>
                <CardTitle className="text-base text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" /> Staff Without Salary Structure ({teachersWithoutStructure.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {teachersWithoutStructure.map(t => (
                    <Badge key={t.id} variant="outline" className="cursor-pointer hover:bg-amber-100" 
                      onClick={() => { setStructureForm({...structureForm, teacher_id: t.id}); setStructureDialog(true); }}>
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Slips Tab */}
        <TabsContent value="slips" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h3 className="text-lg font-semibold">Salary Slips - {formatMonth(selectedMonth)} ({slips.length})</h3>
            <div className="flex flex-wrap gap-2">
              {slips.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={regenerateBulk} data-testid="regenerate-all-slips">
                    <RefreshCw className="h-4 w-4 mr-2" /> Recalculate All
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadBulkPDF} data-testid="download-all-slips">
                    <Download className="h-4 w-4 mr-2" /> Download All
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => setGenerateDialog(true)}>
                <Calculator className="h-4 w-4 mr-2" /> Generate All
              </Button>
            </div>
          </div>

          <div className="grid gap-4">
            {slips.map(slip => (
              <Card key={slip.id} className="hover:shadow-md transition-shadow" data-testid={`slip-${slip.slip_number}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{slip.teacher_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{slip.slip_number}</p>
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                      <div className="text-center p-2 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Attendance</p>
                        <p className="font-semibold">{slip.attendance?.present_days}/{slip.attendance?.working_days}</p>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-green-50 dark:bg-green-950">
                        <p className="text-xs text-muted-foreground">Earnings</p>
                        <p className="font-semibold text-green-600">₹{(slip.earnings?.gross_earnings||0).toLocaleString()}</p>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-red-50 dark:bg-red-950">
                        <p className="text-xs text-muted-foreground">Deductions</p>
                        <p className="font-semibold text-red-600">₹{(slip.deductions?.total_deductions||0).toLocaleString()}</p>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                        <p className="text-xs text-muted-foreground">Net Pay</p>
                        <p className="font-bold text-emerald-600">₹{(slip.net_payable||0).toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-2">
                      <Badge variant={slip.payment_status === 'paid' ? 'default' : 'secondary'}>
                        {slip.payment_status === 'paid' ? '✓ Paid' : 'Pending'}
                      </Badge>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setSelectedSlip(slip); setSlipDetailDialog(true); }} title="View Details">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => regenerateSlip(slip.staff_id || slip.teacher_id)} title="Recalculate">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => downloadPDF(slip.id)} title="Download">
                          <Download className="h-4 w-4" />
                        </Button>
                        {slip.payment_status === 'pending' && (
                          <Button size="icon" variant="ghost" onClick={() => { setSelectedSlip(slip); setPayDialog(true); }} title="Mark Paid">
                            <CreditCard className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {slips.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No salary slips generated for {formatMonth(selectedMonth)}</p>
                  <Button className="mt-4" onClick={() => setGenerateDialog(true)}>
                    <Calculator className="h-4 w-4 mr-2" /> Generate Slips
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Structure Dialog */}
      <Dialog open={structureDialog} onOpenChange={setStructureDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Salary Structure</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <Label>Select Staff Member *</Label>
              <Select value={structureForm.teacher_id} onValueChange={v => setStructureForm({...structureForm, teacher_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.staff_type === 'non_teaching' ? '(Non-Teaching)' : ''} - {t.employee_id || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Earnings */}
              <div className="space-y-4">
                <h4 className="font-medium text-green-600 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" /> Earnings
                </h4>
                <div className="space-y-3">
                  <div><Label>Basic Salary *</Label><Input type="number" value={structureForm.basic_salary} onChange={e => setStructureForm({...structureForm, basic_salary: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>HRA</Label><Input type="number" value={structureForm.hra} onChange={e => setStructureForm({...structureForm, hra: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>DA</Label><Input type="number" value={structureForm.da} onChange={e => setStructureForm({...structureForm, da: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>TA</Label><Input type="number" value={structureForm.ta} onChange={e => setStructureForm({...structureForm, ta: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>Medical Allowance</Label><Input type="number" value={structureForm.medical_allowance} onChange={e => setStructureForm({...structureForm, medical_allowance: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>Special Allowance</Label><Input type="number" value={structureForm.special_allowance} onChange={e => setStructureForm({...structureForm, special_allowance: parseFloat(e.target.value)||0})} /></div>
                </div>
              </div>

              {/* Deductions */}
              <div className="space-y-4">
                <h4 className="font-medium text-red-600 flex items-center gap-2">
                  <Banknote className="h-4 w-4" /> Deductions
                </h4>
                <div className="space-y-3">
                  <div><Label>PF Deduction</Label><Input type="number" value={structureForm.pf_deduction} onChange={e => setStructureForm({...structureForm, pf_deduction: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>Professional Tax</Label><Input type="number" value={structureForm.professional_tax} onChange={e => setStructureForm({...structureForm, professional_tax: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>TDS</Label><Input type="number" value={structureForm.tds} onChange={e => setStructureForm({...structureForm, tds: parseFloat(e.target.value)||0})} /></div>
                  <div><Label>Other Deductions</Label><Input type="number" value={structureForm.other_deductions} onChange={e => setStructureForm({...structureForm, other_deductions: parseFloat(e.target.value)||0})} /></div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <Card className="bg-muted/50">
              <CardContent className="py-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Gross</p>
                    <p className="text-xl font-bold text-green-600">₹{gross.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Deductions</p>
                    <p className="text-xl font-bold text-red-600">₹{deductions.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Salary</p>
                    <p className="text-xl font-bold">₹{net.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter><Button onClick={saveStructure}>Save Structure</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Bulk Dialog */}
      <Dialog open={generateDialog} onOpenChange={setGenerateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Salary Slips</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p>Generate salary slips for all staff with salary structure for <strong>{formatMonth(selectedMonth)}</strong>?</p>
            <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
              <CardContent className="py-3 text-sm">
                <p className="text-blue-800 dark:text-blue-200">
                  <strong>Formula:</strong> Daily Rate = Monthly Gross ÷ Days in Month
                  <br />Net Pay = (Present Days × Daily Rate) - Deductions
                </p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter><Button onClick={generateBulk}>Generate All Slips</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Salary Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Late Threshold (minutes)</Label><Input type="number" value={settingsForm.late_threshold_minutes||15} onChange={e => setSettingsForm({...settingsForm, late_threshold_minutes: parseInt(e.target.value)})} /></div>
            <div><Label>Max Late Days Allowed</Label><Input type="number" value={settingsForm.max_late_days_allowed||3} onChange={e => setSettingsForm({...settingsForm, max_late_days_allowed: parseInt(e.target.value)})} /></div>
            <div><Label>Late Deduction per Day (₹)</Label><Input type="number" value={settingsForm.late_deduction_per_day||50} onChange={e => setSettingsForm({...settingsForm, late_deduction_per_day: parseFloat(e.target.value)})} /></div>
            <div><Label>Overtime Rate per Hour (₹)</Label><Input type="number" value={settingsForm.overtime_rate_per_hour||100} onChange={e => setSettingsForm({...settingsForm, overtime_rate_per_hour: parseFloat(e.target.value)})} /></div>
          </div>
          <DialogFooter><Button onClick={saveSettings}>Save Settings</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark Salary as Paid</DialogTitle></DialogHeader>
          {selectedSlip && (
            <div className="space-y-4">
              <Card className="bg-muted/50">
                <CardContent className="py-4">
                  <p className="font-medium">{selectedSlip.teacher_name}</p>
                  <p className="text-2xl font-bold text-green-600">₹{selectedSlip.net_payable?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <div><Label>Payment Method</Label>
                <Select value={payForm.payment_method} onValueChange={v => setPayForm({...payForm, payment_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reference/Transaction ID</Label><Input value={payForm.payment_reference} onChange={e => setPayForm({...payForm, payment_reference: e.target.value})} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={markPaid}>Mark as Paid</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slip Detail Dialog */}
      <Dialog open={slipDetailDialog} onOpenChange={setSlipDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Salary Slip Details - {formatMonth(selectedSlip?.month)}</DialogTitle>
          </DialogHeader>
          <SlipDetailView slip={selectedSlip} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => regenerateSlip(selectedSlip?.staff_id || selectedSlip?.teacher_id)}>
              <RefreshCw className="h-4 w-4 mr-2" /> Recalculate
            </Button>
            <Button variant="outline" onClick={() => downloadPDF(selectedSlip?.id)}>
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            {selectedSlip?.payment_status === 'pending' && (
              <Button onClick={() => { setSlipDetailDialog(false); setPayDialog(true); }}>
                <CreditCard className="h-4 w-4 mr-2" /> Mark as Paid
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SalarySlipPage;
